import React, { useState, useRef, useEffect } from "react";
import { Rnd } from "react-rnd";
import useLeadCapture from "../UseLeadCapture";

// Portrait Component with its own Rnd and video
const PortraitPlayer = ({ src, redirectUrl, handle }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { handelAlerbarLead } = useLeadCapture();

  const handleVideoPlay = () => {
    setIsPlaying(true);
    setIsVideoEnded(false);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleVideoEnded = () => {
    setIsVideoEnded(true);
  };

  const handelLsqCall = () => {
    const params = {
      img_link: src,
      redirect_link: redirectUrl,
      source: "CTA_PIP_REPORTS"
    };
    handelAlerbarLead(params);
  };

  const handleVisitSite = async () => {
    await handelLsqCall();
    if (handle) {
      handle();
    } else {
      window.open(redirectUrl, "_blank");
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
    };
  }, []);

  if (!isVisible) return null;

  const isMobile = window.innerWidth <= 768;
  const defaultWidth = isMobile ? 150 : 200; // Narrower for portrait
  const defaultHeight = isMobile ? 250 : 350; // Taller for portrait

  return (
    <Rnd
      style={{
        zIndex: 1000,
        position: "fixed",
        top: "-87px",
        ...(isFullscreen && {
          width: "56.25vh", // Portrait fullscreen dimensions
          height: "100vh",
          maxWidth: "100vw",
          maxHeight: "177.78vw",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "black"
        })
      }}
      default={{
        x: 20,
        y: Math.max(window.innerHeight - defaultHeight - 140, 20),
        width: defaultWidth,
        height: defaultHeight
      }}
      lockAspectRatio={9 / 16} // Portrait aspect ratio
      enableResizing={
        !isFullscreen && {
          bottomRight: true,
          topRight: true
        }
      }
      dragHandleClassName="pip-video"
      disableDragging={(isMobile && isPlaying) || isFullscreen}
      disable={isFullscreen}
    >
      <div
        ref={containerRef}
        className="draggable-pip"
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          backgroundColor: isFullscreen ? "black" : "transparent",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <video
          ref={videoRef}
          className="pip-video"
          src={src}
          controls
          playsInline
          controlsList="nodownload"
          onEnded={handleVideoEnded}
          onPlay={handleVideoPlay}
          onPause={handlePause}
          muted
          autoPlay
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            pointerEvents: "auto",
            zIndex: 2,
            position: "relative",
            objectFit: isFullscreen ? "contain" : "cover"
          }}
        />

        {!isFullscreen && (
          <button
            className="close-btn"
            onClick={() => setIsVisible(false)}
            style={{
              position: "absolute",
              top: "6px",
              right: "30px",
              background: "rgba(0, 0, 0, 0.6)",
              border: "none",
              color: "white",
              fontSize: "18px",
              cursor: "pointer",
              zIndex: 5,
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              lineHeight: "18px",
              textAlign: "center",
              padding: 0
            }}
          >
            ✕
          </button>
        )}

        <button
          className="fullscreen-btn"
          onClick={toggleFullscreen}
          style={{
            position: "absolute",
            top: "6px",
            right: isFullscreen ? "30px" : "6px",
            background: "rgba(0, 0, 0, 0.6)",
            border: "none",
            color: "white",
            fontSize: "18px",
            cursor: "pointer",
            zIndex: 5,
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            lineHeight: "18px",
            textAlign: "center",
            padding: 0
          }}
        >
          {isFullscreen ? "⤵" : "⤢"}
        </button>

        {isVideoEnded && (
          <button
            onClick={handleVisitSite}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 16px",
              cursor: "pointer",
              zIndex: 5,
              fontSize: "14px"
            }}
          >
            {handle ? "Visit" : "Visit Site"}
          </button>
        )}
      </div>
    </Rnd>
  );
};

// Landscape Component with its own Rnd and video
const LandscapePlayer = ({ src, redirectUrl, handle }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { handelAlerbarLead } = useLeadCapture();

  const handleVideoPlay = () => {
    setIsPlaying(true);
    setIsVideoEnded(false);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleVideoEnded = () => {
    setIsVideoEnded(true);
  };

  const handelLsqCall = () => {
    const params = {
      img_link: src,
      redirect_link: redirectUrl,
      source: "CTA_PIP_REPORTS"
    };
    handelAlerbarLead(params);
  };

  const handleVisitSite = async () => {
    await handelLsqCall();
    if (handle) {
      handle();
    } else {
      window.open(redirectUrl, "_blank");
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
    };
  }, []);

  if (!isVisible) return null;

  const isMobile = window.innerWidth <= 768;
  const defaultWidth = isMobile ? 220 : 320;
  const defaultHeight = isMobile ? 124 : 180;

  return (
    <Rnd
      style={{
        zIndex: 1000,
        position: "fixed",
        top: "-87px",
        ...(isFullscreen && {
          width: "100vw",
          height: "56.25vw",
          maxHeight: "100vh",
          maxWidth: "177.78vh",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "black"
        })
      }}
      default={{
        x: 20,
        y: Math.max(window.innerHeight - defaultHeight - 140, 20),
        width: defaultWidth,
        height: defaultHeight
      }}
      lockAspectRatio={16 / 9} // Landscape aspect ratio
      enableResizing={
        !isFullscreen && {
          bottomRight: true,
          topRight: true
        }
      }
      dragHandleClassName="pip-video"
      disableDragging={(isMobile && isPlaying) || isFullscreen}
      disable={isFullscreen}
    >
      <div
        ref={containerRef}
        className="draggable-pip"
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          backgroundColor: isFullscreen ? "black" : "transparent",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <video
          ref={videoRef}
          className="pip-video"
          src={src}
          controls
          playsInline
          controlsList="nodownload"
          onEnded={handleVideoEnded}
          onPlay={handleVideoPlay}
          onPause={handlePause}
          muted
          autoPlay
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            pointerEvents: "auto",
            zIndex: 2,
            position: "relative",
            objectFit: isFullscreen ? "contain" : "cover"
          }}
        />

        {!isFullscreen && (
          <button
            className="close-btn"
            onClick={() => setIsVisible(false)}
            style={{
              position: "absolute",
              top: "6px",
              right: "30px",
              background: "rgba(0, 0, 0, 0.6)",
              border: "none",
              color: "white",
              fontSize: "18px",
              cursor: "pointer",
              zIndex: 5,
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              lineHeight: "18px",
              textAlign: "center",
              padding: 0
            }}
          >
            ✕
          </button>
        )}

        <button
          className="fullscreen-btn"
          onClick={toggleFullscreen}
          style={{
            position: "absolute",
            top: "6px",
            right: isFullscreen ? "30px" : "6px",
            background: "rgba(0, 0, 0, 0.6)",
            border: "none",
            color: "white",
            fontSize: "18px",
            cursor: "pointer",
            zIndex: 5,
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            lineHeight: "18px",
            textAlign: "center",
            padding: 0
          }}
        >
          {isFullscreen ? "⤵" : "⤢"}
        </button>

        {isVideoEnded && (
          <button
            onClick={handleVisitSite}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 16px",
              cursor: "pointer",
              zIndex: 5,
              fontSize: "14px"
            }}
          >
            {handle ? "Visit" : "Visit Site"}
          </button>
        )}
      </div>
    </Rnd>
  );
};

// Main Component
const PiPVideoPlayer = ({ src, redirectUrl, handle }) => {
  const videoRef = useRef(null);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (video) {
      const ratio = video.videoWidth / video.videoHeight;
      setAspectRatio(ratio);
    }
  };

  return (
    <>
      <video ref={videoRef} src={src} style={{ display: "none" }} onLoadedMetadata={handleLoadedMetadata} />
      {aspectRatio < 1 ? (
        <PortraitPlayer src={src} redirectUrl={redirectUrl} handle={handle} />
      ) : (
        <LandscapePlayer src={src} redirectUrl={redirectUrl} handle={handle} />
      )}
    </>
  );
};

export default PiPVideoPlayer;
